import { Component } from '@theme/component';

/**
 * Buy it now button.
 *
 * Adds the current selection to the cart, then sends the buyer straight to the
 * checkout — the same destination as Shopify's "More payment options" link, but
 * as a first-class button instead of a footnote under a wallet button. Payment
 * method choice happens on the checkout page, where every option is listed
 * side by side.
 *
 * Availability is not decided here: the button mirrors the Add to cart button,
 * so variant selection, stock and quantity rules stay in one place.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
class InflynBuyItNow extends Component {
  requiredRefs = ['button'];

  /** @type {MutationObserver | undefined} */
  #addToCartObserver;

  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    const addToCartButton = this.#addToCartButton;
    if (!addToCartButton) return;

    this.#syncDisabledState();
    this.#addToCartObserver = new MutationObserver(() => this.#syncDisabledState());
    this.#addToCartObserver.observe(addToCartButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#addToCartObserver?.disconnect();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.closest('form');
  }

  /** @returns {HTMLButtonElement | null} */
  get #addToCartButton() {
    return this.#form?.querySelector('button[ref="addToCartButton"]') ?? null;
  }

  #syncDisabledState() {
    const addToCartButton = this.#addToCartButton;
    if (!addToCartButton || this.#submitting) return;

    this.refs.button.disabled = addToCartButton.disabled;
  }

  /**
   * @param {Event} event
   */
  async handleClick(event) {
    event.preventDefault();

    const form = this.#form;
    const { button } = this.refs;
    if (!form || button.disabled || this.#submitting) return;

    this.#submitting = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.dataset.cartAddUrl ?? '/cart/add', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.description || result.message || '');

      // Leave the button disabled — the page is on its way out.
      window.location.assign(this.dataset.checkoutUrl ?? '/checkout');
    } catch (error) {
      this.#submitting = false;
      button.removeAttribute('aria-busy');
      this.#syncDisabledState();
      this.#showError(error instanceof Error ? error.message : '');
    }
  }

  /**
   * Reuses the error slot the buy-buttons block already renders for Add to cart.
   *
   * @param {string} message
   */
  #showError(message) {
    const errorElement = this.#form?.querySelector('[ref="addToCartTextError"]');
    const text = message || this.dataset.genericErrorText || '';

    if (!(errorElement instanceof HTMLElement)) {
      console.error('[inflyn-buy-it-now]', text);
      return;
    }

    errorElement.classList.remove('hidden');

    // The slot's first child is the error icon; the message rides after it.
    const existing = Array.from(errorElement.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (existing) {
      existing.textContent = ` ${text}`;
    } else {
      errorElement.appendChild(document.createTextNode(` ${text}`));
    }
  }
}

if (!customElements.get('inflyn-buy-it-now')) {
  customElements.define('inflyn-buy-it-now', InflynBuyItNow);
}
